/**
 * Feature: Proposal Builder with E-Signatures
 * /features/proposals
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { FileText, Layers, Camera, Pen, Package, Eye, ArrowRight } from 'lucide-react';

const faqs = [
  {
    question: 'Are e-signatures legally binding?',
    answer:
      'Yes. E-signatures on Rooflyzer are powered by DocuSeal and comply with ESIGN Act and UETA standards — legally binding in all 50 US states.',
  },
  {
    question: 'Can I include photos in proposals?',
    answer:
      'Yes. Attach job site photos, satellite imagery, damage documentation, and material samples to any proposal.',
  },
  {
    question: 'Can I create proposal templates?',
    answer:
      'Yes. Save material packages, labor rates, terms, and standard language as reusable templates. Build a new proposal in under 2 minutes.',
  },
  {
    question: 'Do customers need to create an account to sign?',
    answer: 'No. Customers sign via a shareable link — no account required, no app to download.',
  },
  {
    question: 'Can I send proposals from mobile?',
    answer:
      'Yes. Build and send proposals from your phone on the job site using our mobile-first interface.',
  },
];

export const ProposalsPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Proposal Builder with E-Signatures',
    description:
      'Build professional roofing proposals in minutes. Include satellite photos, material options, and digital signatures. Close more jobs in the field.',
    featureList: [
      'Drag-and-drop proposal builder',
      'Multi-option proposals',
      'Satellite photo integration',
      'One-tap e-signatures',
      'Reusable material packages',
      'Live proposal tracking',
    ],
    url: 'https://rooflyzer.pro/features/proposals',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Proposal Builder with E-Signatures"
        description="Build professional roofing proposals in minutes. Include satellite photos, material options, and digital signatures. Close more jobs in the field."
        canonicalUrl="/features/proposals"
        keywords="roofing proposal software, roofing proposal builder, roofing e-signature, contractor proposal software"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Proposal Builder', url: '/features/proposals' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={FileText}>Close More in the Field</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Roofing Proposals That Win Jobs{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Before You Leave the Driveway
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Build professional, multi-option proposals with satellite photos, material packages,
              and one-tap e-signatures. Your customer signs on the spot — no waiting, no
              back-and-forth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Build a Proposal Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/demo">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See Sample Proposal
                </Button>
              </Link>
            </div>
          </section>

          {/* Product Screenshot */}
          <section className="mb-24">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src="/product-frames/estimates.webp"
                alt="Rooflyzer proposals and estimates interface"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm text-white/90 border border-white/20">
                  Live product screenshot
                </span>
              </div>
            </div>
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Everything in One Proposal
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: FileText,
                  title: 'Drag-and-Drop Builder',
                  desc: 'Add line items, packages, photos, and terms with a simple editor. No design skills needed — just fill in the details.',
                },
                {
                  icon: Layers,
                  title: 'Multi-Option Proposals',
                  desc: 'Present Good/Better/Best options so customers choose their own price point. Increases average deal size by 22%.',
                },
                {
                  icon: Camera,
                  title: 'Satellite Photo Integration',
                  desc: "Pull in aerial photos of the property automatically. Show customers exactly what you're replacing and why.",
                },
                {
                  icon: Pen,
                  title: 'One-Tap E-Signatures',
                  desc: 'Customers sign with a finger on their phone or tablet. DocuSeal-powered, legally binding in all 50 states.',
                },
                {
                  icon: Package,
                  title: 'Reusable Material Packages',
                  desc: 'Save your most common material configurations as templates. Populate a full proposal in under 60 seconds.',
                },
                {
                  icon: Eye,
                  title: 'Live Proposal Tracking',
                  desc: 'Know when your customer opened the proposal, how many times they viewed it, and when they signed.',
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-slate-500/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="size-6 text-slate-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Manually type proposals in Word or Excel',
                    'Print PDFs, email, wait days for a response',
                    'Chase physical signatures through DocuSign back-and-forth',
                    'Lose jobs to competitors who follow up faster',
                    'No idea if the customer even opened the proposal',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Rooflyzer Way</h2>
                <FeatureChecklist
                  items={[
                    'Build in 2 minutes from reusable templates',
                    'Satellite photos auto-populated from the address',
                    'Customer signs on the spot — job booked before you leave',
                    'Proposal open tracking tells you when to follow up',
                    'Multi-option packages increase average close value',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats
            title="Real Numbers"
            items={[
              {
                value: '2 minutes',
                label: 'Average Proposal Build Time',
                desc: 'With saved templates',
              },
              { value: '31%', label: 'Higher Close Rate', desc: 'With multi-option proposals' },
              {
                value: 'Same day',
                label: 'Most Proposals Signed',
                desc: 'Within hours of delivery',
              },
              { value: '100%', label: 'Legally Binding', desc: 'In all 50 US states' },
            ]}
          />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* Bottom CTA */}
          <FeatureSignup
            title="Ready to Close More Jobs in the Field?"
            buttonLabel="Build a Proposal Free"
          >
            Build your first proposal in under 2 minutes. No credit card required.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default ProposalsPage;
